'use client';

import * as React from 'react';
import { cx } from '../utils';
import { JIcon } from '../../icons';
import { devWarn } from '../internal/devWarn';
import { Field } from './Field';
import type { StyledProps } from '../types';
import type { UnsafeStyleProps } from '../escape-hatches';

export interface ColorSwatch {
  /** Canonical lower-case hex `#rrggbb`. */
  value: string;
  /** Adopter-supplied name used for screen readers and CV-safe identification. */
  label: string;
}

export type ColorPickerSize = 'sm' | 'md';

export interface ColorPickerProps extends StyledProps, UnsafeStyleProps {
  swatches: ColorSwatch[];
  /**
   * Current value (canonical `#rrggbb`). The swatch grid is **controlled**:
   * selection is driven by `value`, which you update from `onChange`. There is
   * no uncontrolled / `defaultValue` mode — without `value`, no swatch shows
   * selected (a not-yet-selected picker is a valid state).
   */
  value?: string;
  onChange?: (hex: string) => void;
  /** Render an opt-in hex input below the swatch grid. */
  withInput?: boolean;
  /** Fired when input transitions between valid and invalid hex. */
  onValidationChange?: (valid: boolean) => void;
  /** Visual size: 32 px (sm) or 44 px (md). */
  size?: ColorPickerSize;
  disabled?: boolean;
  /** Adopter `aria-label` for the radiogroup. */
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  testId?: string;
}

const HEX_RE = /^#[0-9a-f]{6}$/;

function normalize(hex: string | undefined): string | undefined {
  if (!hex) return undefined;
  const lower = hex.toLowerCase();
  return HEX_RE.test(lower) ? lower : undefined;
}

/**
 * Pick a foreground token for the check overlay so it meets WCAG
 * 1.4.11 (non-text contrast, 3:1 minimum) against the swatch
 * background. A bare luminance midpoint cutoff (`L > 0.5`) was
 * incorrect — mid-luminance brand colors like `#10b981` /
 * `#ff6b35` (L ≈ 0.23–0.31) picked white but only landed at
 * ~2.5:1 contrast. Compare the actual contrast ratios produced
 * by neutral-100 vs neutral-900 and pick whichever hits 3:1 first;
 * prefer the higher ratio when both qualify.
 */
function checkFg(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const bgL = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  /* Token-aligned approximations (v5.4 11-tier neutrals).
     `--color-neutral-100` ≈ #f8f8f9, `--color-neutral-900` ≈ #1a1b20;
     both relative luminances are well-known constants. */
  const Llight = 0.973; // luminance of #f8f8f9 (matches old #fbfbfc to 3 dp)
  const Ldark = 0.01; // luminance of #1a1b20
  const ratio = (a: number, b: number) => (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
  const lightRatio = ratio(bgL, Llight);
  const darkRatio = ratio(bgL, Ldark);
  return lightRatio >= darkRatio ? 'var(--color-neutral-100)' : 'var(--color-neutral-900)';
}

export function ColorPicker({
  swatches,
  value,
  onChange,
  withInput = false,
  onValidationChange,
  size = 'md',
  disabled = false,
  className,
  unsafe_style,
  testId,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}: ColorPickerProps) {
  const normalValue = normalize(value);
  const [inputValue, setInputValue] = React.useState(normalValue ?? '');
  const [inputValid, setInputValid] = React.useState(true);
  const swatchRefs = React.useRef<Map<string, HTMLButtonElement>>(new Map());

  /* Stash the callback in a ref so the sync-effect doesn't depend on its
     identity. Inline arrow callbacks would otherwise re-trigger this
     effect on every parent render and reset the user's in-progress hex
     typing back to the prop value. */
  const onValidationChangeRef = React.useRef(onValidationChange);
  React.useEffect(() => {
    onValidationChangeRef.current = onValidationChange;
  });
  React.useEffect(() => {
    setInputValue(normalValue ?? '');
    setInputValid((prev) => {
      if (!prev) onValidationChangeRef.current?.(true);
      return true;
    });
  }, [normalValue]);

  if (value && !normalValue) {
    devWarn(
      `[JKit.ColorPicker] value "${value}" is not a canonical lower-case ` +
        '#RRGGBB hex. The component normalises to lowercase but rejects ' +
        'shorthand (#fff) and uppercase. No swatch is selected.',
    );
  }

  /* Codex iter-4 #high: invalid swatch values used to be lowercased
     and rendered as enabled radios, written into `--swatch-bg`, and
     emitted through onChange. A swatch like
     `url(https://attacker.example/pixel)` would be consumed by
     `background: var(--swatch-bg)` and trigger a browser fetch.
     Even benign bad values poison the `#rrggbb` onChange contract.

     Sanitize once per `swatches` identity change: keep only entries
     whose value matches HEX_RE after lowercasing; everything else is
     dropped with a single dev-warn per (bad value) pair. The
     downstream render loop never sees a non-canonical hex. */
  const _swatchWarnedRef = React.useRef<Set<string>>(new Set());
  const safeSwatches = React.useMemo(() => {
    const out: ColorSwatch[] = [];
    for (const s of swatches) {
      const lower = s.value.toLowerCase();
      if (HEX_RE.test(lower)) {
        /* Normalise to lowercase canonical form so onChange always
           emits the contracted shape. */
        out.push({ ...s, value: lower });
      } else if (!_swatchWarnedRef.current.has(s.value)) {
        _swatchWarnedRef.current.add(s.value);
        devWarn(
          `[JKit.ColorPicker] swatch value "${s.value}" is not canonical ` +
            '#RRGGBB; dropped from the rendered palette to keep the ' +
            'onChange contract honest and prevent CSS-injection via ' +
            '--swatch-bg.',
        );
      }
    }
    return out;
  }, [swatches]);

  /* Pick the tabbable swatch from the RENDERED set. Using `normalValue`
     directly used to leave the radiogroup keyboard-unreachable when
     `value` was syntactically valid but not present in `swatches`
     (Codex iter-3 #medium). Now we validate against the palette and
     fall back to the first swatch. */
  const tabbableValue = (() => {
    const lowerValues = safeSwatches.map((s) => s.value);
    if (normalValue && lowerValues.includes(normalValue)) return normalValue;
    return lowerValues[0];
  })();

  /* Roving-tabindex anchor. Driving tabIndex from `value` alone desyncs in
     uncontrolled/frozen-value usage: arrow nav moves DOM focus (and emits
     onChange) but if the parent doesn't echo `value` back, the tabbable swatch
     stays on the old one — so the focused swatch is tabIndex=-1 and Tab exits
     from the wrong element (the SegmentedControl-class bug). Track the
     focused swatch and let it lead; fall back to the value-derived default
     before any interaction (and if the active swatch leaves the palette). */
  const [activeSwatch, setActiveSwatch] = React.useState<string | null>(null);
  const rovingValue =
    activeSwatch && safeSwatches.some((s) => s.value === activeSwatch)
      ? activeSwatch
      : tabbableValue;

  const focusNeighbor = (current: string, delta: 1 | -1) => {
    const idx = safeSwatches.findIndex((s) => s.value === current);
    if (idx < 0) return;
    const nextIdx = (idx + delta + safeSwatches.length) % safeSwatches.length;
    const nextHex = safeSwatches[nextIdx]!.value;
    onChange?.(nextHex);
    /* setTimeout 0 lets the controlled-value sync re-render before focus. */
    queueMicrotask(() => swatchRefs.current.get(nextHex)?.focus());
  };

  const onSwatchKey = (e: React.KeyboardEvent, hex: string) => {
    if (disabled) return;
    let handled = true;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') focusNeighbor(hex, 1);
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') focusNeighbor(hex, -1);
    else if (e.key === 'Home') {
      const first = safeSwatches[0]?.value;
      if (first) {
        onChange?.(first);
        queueMicrotask(() => swatchRefs.current.get(first)?.focus());
      }
    } else if (e.key === 'End') {
      const last = safeSwatches[safeSwatches.length - 1]?.value;
      if (last) {
        onChange?.(last);
        queueMicrotask(() => swatchRefs.current.get(last)?.focus());
      }
    } else if (e.key === ' ') {
      onChange?.(hex);
    } else {
      handled = false;
    }
    if (handled) e.preventDefault();
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setInputValue(next);
    const lower = next.toLowerCase();
    if (next.length === 0) {
      /* Empty is a NEUTRAL / not-yet-validated state — it must NOT report
         invalid (aria-invalid is gated on length>0, so reporting
         onValidationChange(false) here contradicted the rendered validity).
         Clearing a previously-invalid field transitions back to "no error". */
      if (!inputValid) {
        setInputValid(true);
        onValidationChange?.(true);
      }
      return;
    }
    const valid = HEX_RE.test(lower);
    if (valid !== inputValid) {
      setInputValid(valid);
      onValidationChange?.(valid);
    }
    if (valid) onChange?.(lower);
  };

  return (
    <div
      className={cx('j-color-picker', `j-color-picker-${size}`, className)}
      style={unsafe_style}
      data-jq-id={testId}
      role="radiogroup"
      aria-label={ariaLabelledBy ? undefined : ariaLabel || 'Choose a color'}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      aria-disabled={disabled || undefined}
      onBlur={(e) => {
        /* When focus leaves the grid entirely, drop the roving anchor so the
           next entry (and any external `value` change made while focus was
           away) lands on the selected swatch, not a stale focused one.
           relatedTarget is the reliable signal when present; when it's null
           (cross-context / some engines) fall back to checking the settled
           activeElement so we don't over-reset mid intra-group arrow nav. */
        const next = e.relatedTarget as Node | null;
        if (next) {
          if (!e.currentTarget.contains(next)) setActiveSwatch(null);
          return;
        }
        const group = e.currentTarget;
        queueMicrotask(() => {
          if (!group.contains(document.activeElement)) setActiveSwatch(null);
        });
      }}
    >
      <div className="j-color-picker-grid" role="presentation">
        {safeSwatches.map((s) => {
          const hex = s.value.toLowerCase();
          const checked = hex === normalValue;
          const fg = checkFg(hex);
          return (
            <button
              key={hex}
              type="button"
              role="radio"
              ref={(el) => {
                if (el) swatchRefs.current.set(hex, el);
                else swatchRefs.current.delete(hex);
              }}
              className="j-color-picker-swatch"
              aria-checked={checked}
              aria-label={`${s.label} ${hex}`}
              data-checked={checked || undefined}
              data-testid-color={hex}
              tabIndex={hex === rovingValue ? 0 : -1}
              onFocus={() => setActiveSwatch(hex)}
              disabled={disabled}
              /* `--swatch-bg` / `--swatch-fg` are component-level slot
                 variables consumed by .j-color-picker-swatch CSS, NOT
                 raw style values. `--swatch-bg` is the data (the
                 swatch's color); `--swatch-fg` resolves to a Jacquard
                 neutral token via checkFg(). */
              style={
                {
                  '--swatch-bg': hex,
                  '--swatch-fg': fg,
                } as React.CSSProperties
              }
              onClick={() => !disabled && onChange?.(hex)}
              onKeyDown={(e) => onSwatchKey(e, hex)}
            >
              {checked && <JIcon intent="check" size={size === 'sm' ? 12 : 16} aria-hidden />}
            </button>
          );
        })}
      </div>
      {withInput && (
        <Field
          className={cx(
            'j-color-picker-input-field',
            !inputValid && inputValue.length > 0 && 'j-color-picker-input-error',
          )}
          disabled={disabled}
          prefix={
            <span
              className="j-color-picker-input-chip"
              aria-hidden
              style={
                {
                  '--swatch-bg':
                    inputValid && inputValue.length > 0
                      ? inputValue.toLowerCase()
                      : (normalValue ?? 'transparent'),
                } as React.CSSProperties
              }
            />
          }
        >
          <input
            type="text"
            className="j-input j-color-picker-input"
            value={inputValue}
            onChange={onInputChange}
            placeholder="#000000"
            maxLength={7}
            spellCheck={false}
            autoComplete="off"
            disabled={disabled}
            aria-invalid={!inputValid && inputValue.length > 0}
            aria-label="Hex code"
            inputMode="text"
          />
        </Field>
      )}
    </div>
  );
}
